import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useTranslation } from 'react-i18next';
import { ChevronDown } from "lucide-react";
import { resetCookieConsent } from '@/components/CookieConsent';
import { useLocalizedPath } from "@/hooks/useLocalizedPath";
import { stripLangPrefix } from '@/components/LangWrapper';
import '@/pages/landing-animations.css';
import { useHostBranding } from '@/components/HostBranding';
import { isNonTranslatablePath } from '@/lib/nonTranslatablePaths';

// Locale switcher data — added 2026-05-24 after multi-region SEO diagnosis
// revealed Google indexed 1 of ~4,200 /pl URLs and 0 of /de. Root cause:
// hreflang HEAD tags exist but the EN homepage had ZERO <a> anchors to any
// locale (53 anchors, 0 pointing to /pl, /de, /es, /it, /fr…). Without
// PR-flow from the root, all 22 locale subtrees were discovery islands.
// This block emits one <a href> per locale in SSR HTML so Google can crawl
// + weight every locale homepage from every page. Native script names so
// the anchor text is also a localized signal (Polski vs Polish, etc.).
// Alphabetical by locale code (canonical order across all language lists).
const LOCALES: { code: string; name: string }[] = [
  { code: 'ar', name: 'العربية' },
  { code: 'bg', name: 'Български' },
  { code: 'cs', name: 'Čeština' },
  { code: 'da', name: 'Dansk' },
  { code: 'de', name: 'Deutsch' },
  { code: 'el', name: 'Ελληνικά' },
  { code: 'en', name: 'English' },
  { code: 'es', name: 'Español' },
  { code: 'et', name: 'Eesti' },
  { code: 'fi', name: 'Suomi' },
  { code: 'fil', name: 'Filipino' },
  { code: 'fr', name: 'Français' },
  { code: 'hi', name: 'हिन्दी' },
  { code: 'hr', name: 'Hrvatski' },
  { code: 'hu', name: 'Magyar' },
  { code: 'id', name: 'Bahasa Indonesia' },
  { code: 'it', name: 'Italiano' },
  { code: 'ja', name: '日本語' },
  { code: 'ko', name: '한국어' },
  { code: 'lt', name: 'Lietuvių' },
  { code: 'lv', name: 'Latviešu' },
  { code: 'nl', name: 'Nederlands' },
  { code: 'pl', name: 'Polski' },
  { code: 'pt-br', name: 'Português (BR)' },
  { code: 'pt-pt', name: 'Português (PT)' },
  { code: 'ro', name: 'Română' },
  { code: 'sk', name: 'Slovenčina' },
  { code: 'sl', name: 'Slovenščina' },
  { code: 'sv', name: 'Svenska' },
  { code: 'th', name: 'ไทย' },
  { code: 'tr', name: 'Türkçe' },
  { code: 'uk', name: 'Українська' },
  { code: 'vi', name: 'Tiếng Việt' },
];

interface FooterColumn {
  title: string;
  links: { to: string; label: string; external?: boolean }[];
}

export const MarketingFooter = () => {
  const { t } = useTranslation();
  const brand = useHostBranding();
  const [openCol, setOpenCol] = useState<number | null>(null);
  const lp = useLocalizedPath();
  const location = useLocation();

  // basePath = current pathname stripped of language prefix + trailing slash.
  // Used so the locale switcher links to the SAME page in another locale, not
  // always back to that locale's home. Matches HreflangTags.tsx logic so
  // <link rel="alternate"> and visible <a> point to identical URLs (Google
  // consistency signal).
  const basePath = stripLangPrefix(location.pathname).replace(/\/+$/, '');
  const localeHref = (code: string): string =>
    code === 'en' ? (basePath || '/') : `/${code}${basePath}`;

  // Hide locale switcher on protected/non-translatable routes to avoid pointing
  // Google at URLs with no route. Shares one list with HreflangTags so the two
  // cannot drift again — this file's own copy had gone stale and was emitting
  // 15,246 crawlable links to non-existent /{lang}/nvr/* pages.
  const skipLocaleSwitcher = isNonTranslatablePath(basePath);
  // English-only pages are linked from English pages only, so a locale footer
  // carries no English labels.
  const isEnglishPage = location.pathname.replace(/\/+$/, '') === basePath;

  const columns: FooterColumn[] = [
    {
      title: t('footer_product', 'Product'),
      links: [
        { to: lp("/features"), label: t('footer_features', 'Features') },
        { to: lp("/how-it-works"), label: t('footer_how_it_works', 'How It Works') },
        { to: lp("/security-camera-design-tool"), label: t('footer_security_camera_design_tool', 'Security Camera Design Tool') },
        { to: lp("/pricing"), label: t('footer_pricing', 'Pricing') },
        { to: lp("/free"), label: t('free_plan_nav', 'Free plan') },
        // Opens the installer-deliverable channel (2026-09-10). A new landing
        // with no inbound internal links settles where /cctv-layout-software
        // did, at position 72, so it goes in the footer from day one.
        { to: lp("/cctv-quotation-template"), label: t('footer_quotation_template', 'Quotation Template') },
        { to: lp("/calculators"), label: t('footer_calculators', 'Calculators') },
        { to: lp("/calculator/fov"), label: t('footer_fov_calculator', 'FOV Calculator') },
        { to: lp("/calculator/dori"), label: t('footer_dori_calculator', 'DORI Calculator') },
        // SEO landings (2026-05-24): root-slug calculator pages targeting
        // head terms like "lens calculator cctv". Linked from the footer
        // so they get site-wide PageRank from every other page.
        { to: lp("/calculator/lens-selector"), label: t('footer_lens_calculator', 'Lens Calculator') },
        { to: lp("/calculator/storage"), label: t('footer_storage_calculator', 'Storage Calculator') },
        { to: lp("/calculator/bandwidth"), label: t('footer_bandwidth_calculator_landing', 'Bandwidth Calculator') },
        { to: lp("/calculator/poe"), label: t('footer_poe_calculator', 'PoE Budget Calculator') },
        { to: lp("/cameras"), label: t('footer_camera_catalog', 'Camera Catalog') },
        // The only site-wide entry point into the 693 recorder pages, which
        // were link orphans until 2026-08-17. `lp()` leaves it unprefixed: the
        // hub is English-only and sits on the shared non-translatable list.
        { to: lp("/nvr"), label: t('footer_nvr_catalog', 'NVR & Recorder Catalog') },
        ...(isEnglishPage ? [
          { to: "/rtsp", label: "RTSP Streams Explained" },
          { to: "/rtsp-url", label: "RTSP URL Formats" },
          { to: "/onvif", label: "ONVIF Profiles Explained" },
          { to: "/onvif-device-manager", label: "ONVIF Device Manager Guide" },
          { to: "/nvr-vs-dvr", label: "NVR vs DVR" },
          { to: "/poe-switch-for-ip-cameras", label: "PoE Switch for IP Cameras" },
          { to: "/t568a-vs-t568b", label: "T568A vs T568B" },
          { to: "/cat6-max-length", label: "Cat 6 Max Length" },
        ] : []),
      ],
    },
    {
      title: t('footer_resources', 'Resources'),
      links: [
        { to: lp("/blog"), label: t('footer_blog', 'Blog') },
        { to: lp("/blog/cctv-installation-best-practices"), label: t('footer_install_best_practices', 'Installation Best Practices') },
        { to: lp("/docs"), label: t('footer_docs', 'Documentation') },
        { to: lp("/examples"), label: t('footer_examples', 'Design Examples') },
        { to: lp("/use-cases"), label: t('footer_use_cases', 'Use Cases') },
        { to: lp("/faq"), label: t('footer_faq', 'FAQ') },
        { to: lp("/glossary"), label: t('footer_glossary', 'Glossary') },
        { to: lp("/en-62676-4-calculator"), label: t('footer_en_62676', 'EN 62676-4 Standard') },
        ...(isEnglishPage ? [{ to: "/en-50131", label: "EN 50131 Alarm Grades" }, { to: "/gdpr-cctv", label: "GDPR and CCTV" }] : []),
        { to: lp("/johnson-criteria-calculator"), label: t('footer_johnson', 'Johnson Criteria') },
        { to: lp("/camera-physics"), label: t('footer_camera_physics', 'Camera Physics') },
        { to: lp("/ndaa-cctv-compliance"), label: t('footer_ndaa', 'NDAA Section 889') },
      ],
    },
    {
      // Pillar pages from the 2026-05-16 repositioning sprint. Separate
      // column keeps the existing footer structure intact while making
      // the new "security disciplines" surface area discoverable.
      title: t('footer_disciplines', 'Security Disciplines'),
      links: [
        { to: lp("/access-control-design"), label: t('footer_access_control', 'Access Control Design') },
        { to: lp("/intrusion-detection-design"), label: t('footer_intrusion_detection', 'Intrusion Detection Design') },
        { to: lp("/network-planning-for-security"), label: t('footer_network_planning', 'Network Planning') },
        { to: lp("/security-compliance"), label: t('footer_security_compliance', 'Security Compliance') },
      ],
    },
    {
      title: t('footer_compare', 'Compare & Alternatives'),
      links: [
        { to: lp("/jvsg-alternative"), label: t('footer_jvsg_alt', 'JVSG Alternative') },
        { to: lp("/ipvm-alternative"), label: t('footer_ipvm_alt', 'IPVM Alternative') },
        { to: lp("/cctvdesigntool-alternative"), label: t('footer_cdt_alt', 'CCTVDesignTool Alternative') },
        { to: lp("/compare/cctvplanner-vs-jvsg"), label: t('footer_vs_jvsg', 'vs JVSG') },
        { to: lp("/compare/cctvplanner-vs-ipvm"), label: t('footer_vs_ipvm', 'vs IPVM Calculator') },
        { to: lp("/compare/cctvplanner-vs-cctvdesigntool"), label: t('footer_vs_cdt', 'vs CCTV Design Tool') },
        { to: lp("/axis-site-designer-alternative"), label: t('footer_axis_alt', 'AXIS Site Designer Alternative') },
        { to: lp("/hikvision-hitools-alternative"), label: t('footer_hik_alt', 'Hikvision HiTools Alternative') },
        { to: lp("/dahua-design-tool-alternative"), label: t('footer_dahua_alt', 'Dahua Design Tool Alternative') },
        { to: lp("/flir-raven-alternative"), label: t('footer_flir_alt', 'FLIR Raven Alternative') },
        { to: lp("/i-pro-sdt-alternative"), label: t('footer_ipro_alt', 'i-PRO SDT Alternative') },
        ...(isEnglishPage ? [
          { to: "/system-surveyor-alternative", label: "System Surveyor Alternative" },
          { to: "/siteowl-alternative", label: "SiteOwl Alternative" },
          { to: "/videocad-alternative", label: "VideoCAD Alternative" },
        ] : []),
      ],
    },
    {
      title: t('footer_legal', 'Legal & Other'),
      links: [
        { to: lp("/about"), label: t('footer_about', 'About') },
        { to: lp("/partners"), label: t('common:footer_partners', 'Partners') },
        { to: lp("/changelog"), label: t('footer_changelog', 'Changelog') },
        { to: "/attributions", label: t('footer_attributions', 'Attributions') },
        { to: lp("/rodo"), label: t("footer_privacy_policy", "Privacy Policy") },
        { to: lp("/terms"), label: t("footer_terms", "Terms of Service") },
        { to: lp("/feedback"), label: t('footer_feedback', 'Send Feedback') },
        { to: lp("/cctv-design-tool"), label: t('footer_cctv_design_tool', 'CCTV Design Tool') },
        { to: lp("/cctv-layout"), label: t('footer_cctv_layouts', 'CCTV Layouts') },
        { to: lp("/lens/2.8"), label: t('footer_lens_fov_guide', 'Lens FOV Guide') },
      ],
    },
  ];

  const toggleCol = (idx: number) => {
    setOpenCol(openCol === idx ? null : idx);
  };

  return (
    <footer className="pelco-footer">
      <div className="pelco-container" style={{ paddingTop: '3rem', paddingBottom: '2rem' }}>
        {/* Nav columns: our marketing, which a branded host does not serve. */}
        {!brand && (
        <div className="pelco-footer__nav">
          {columns.map((col, idx) => (
            <div key={idx} className="pelco-footer__col">
              {/* Desktop heading */}
              <span className="pelco-footer__heading hidden md:block">
                {col.title}
              </span>
              {/* Mobile accordion heading */}
              <span
                className="pelco-footer__heading md:hidden"
                onClick={() => toggleCol(idx)}
                aria-expanded={openCol === idx}
                role="button"
              >
                {col.title}
                <ChevronDown className="chevron w-4 h-4" />
              </span>
              {/* Links */}
              <nav className={`md:block ${openCol === idx ? 'pelco-footer__links--expanded' : 'pelco-footer__links--collapsed'}`}>
                {col.links.map((link) => (
                  <Link
                    key={link.to}
                    to={link.to}
                    className="pelco-footer__link"
                  >
                    {link.label}
                  </Link>
                ))}
              </nav>
            </div>
          ))}
        </div>
        )}

        {/* Locale switcher — SSR-visible <a> anchors so Google can crawl every
            locale homepage from any page. Added 2026-05-24 to fix the
            zero-rank in non-EN markets per multi-region diagnostic.
            Uses plain <a> rather than <Link> so the href is honest static
            HTML (no client-side interception), matching how hreflang HEAD
            tags resolve to URLs. */}
        {!brand && !skipLocaleSwitcher && (
          <nav
            aria-label={t('footer_locale_nav_label', 'Choose language')}
            className="mt-8 pt-6 border-t"
            style={{ borderColor: 'var(--color-gray-border)' }}
          >
            <h3 className="text-[11px] uppercase tracking-[0.16em] font-mono text-neutral-500 dark:text-brand-text-secondary text-center mb-4">
              {t('footer_locale_heading', 'Language')}
            </h3>
            <div className="flex flex-wrap justify-center gap-x-4 gap-y-2 text-[12px]">
              {LOCALES.map((loc) => (
                <a
                  key={loc.code}
                  href={localeHref(loc.code)}
                  hrefLang={loc.code === 'pt-br' ? 'pt-BR' : loc.code === 'pt-pt' ? 'pt-PT' : loc.code}
                  className="pelco-footer__link"
                  style={{ padding: 0 }}
                >
                  {loc.name}
                </a>
              ))}
            </div>
          </nav>
        )}

        {/* EU signature strip */}
        <div
          className="mt-8 pt-6 border-t text-[11px] leading-relaxed text-neutral-500 dark:text-brand-text-secondary text-center"
          style={{ borderColor: 'var(--color-gray-border)', fontFamily: 'var(--font-mono)' }}
        >
          <p className="tracking-[0.08em]">
            {t('common:eu_footer_tagline', 'Designed, built, and hosted entirely in the European Union. Your data never leaves the EU.')}
          </p>
        </div>

        {/* Bottom bar */}
        <div className="pelco-footer__bottom flex flex-col sm:flex-row items-center justify-between gap-4 mt-6">
          <span>{brand ? (brand.footerText || `© 2026 ${brand.name}`) : <>&copy; 2026 CCTVplanner.io &middot; by DEFENSAR &middot; {t('common:eu_footer_signature', 'Made & hosted in EU')}</>}</span>
          <div className="flex items-center gap-4">
            <button
              onClick={resetCookieConsent}
              className="pelco-footer__link"
              style={{ padding: 0, background: 'none', border: 'none', cursor: 'pointer' }}
            >
              {t('footer_cookie_settings', 'Cookie Settings')}
            </button>
            <Link to={lp("/rodo")} className="pelco-footer__link" style={{ padding: 0 }}>
              {t('footer_privacy_policy', 'Privacy Policy')}
            </Link>
            <Link to={lp("/terms")} className="pelco-footer__link" style={{ padding: 0 }}>
              {t('footer_terms', 'Terms of Service')}
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default MarketingFooter;
